import React from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Buffer } from 'buffer';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { strings } from '../../resources/strings';
import { SeparationType } from '../../enums/SeparationType';
import { ProfileType } from '../../enums/ProfileType';
import { ShopLocation } from '../../models/ShopLocation';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { MainActions } from '../../actions/MainActions';
import { GenerateGroupedGeneralOrdersReportInput } from '../../services/Reports/GenerateGroupedGeneralOrdersReport/GenerateGroupedGeneralOrdersReportInput';
import { Button } from '../../common-components/Button/Button';
import { Field, FieldType } from '../../common-components/Field/Field';
import { If } from '../../common-components/If/If';
import { FieldGroup, FieldLabel, Row } from './styles';

interface IModalGroupedGeneralOrderReportProps {
  close: () => void;
}

const ReportAlert = () => (
  <Alert variant='warning'>
    <strong>Atenção!</strong> Como o relatório precisa de todos os dados dos pedidos a geração pode demorar demasiadamente.
  </Alert>
);

const MainFilterOptions = {
  OnlyOpenOrders: 0,
  OnlyCancelledOrders: 1,
  OpenOrClosedOrders: 2
}

const ModalGroupedGeneralOrderReport: React.FC<IModalGroupedGeneralOrderReportProps> = ({
  close
}) => {

  const step = 50;

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { userLocations, session } = useAuth();

  const [initialDate, setInitialDate] = React.useState<Date | null>(new Date());
  const [finalDate, setFinalDate] = React.useState<Date | null>(new Date());
  const [locationId, setLocationId] = React.useState<number | null>(userLocations[0]);
  const [locations, setLocations] = React.useState<ShopLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = React.useState<boolean>(true);
  const [mainFilterId, setMainFilterId] = React.useState<number | null>(MainFilterOptions.OnlyOpenOrders);
  const [separationType, setSeparationType] = React.useState<number | null>(
    session?.user?.profileType === ProfileType.Vendor ? SeparationType.Prescription : 0);
  const [generating, setGenerating] = React.useState<boolean>(false);
  const [canceling, setCanceling] = React.useState<boolean>(false);
  const [percent, setPercent] = React.useState<number>(0);

  const orderIdList = React.useRef<number[] | null>(null);
  const stringDataList = React.useRef<string[]>([]);
  const cancel = React.useRef(false);

  const {
    generateGroupedGeneralOrdersReportService,
    getOrderIdsForGroupedGeneralReportService,
    toastService,
    reportService,
    getLocationListService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    getLocations();
  }, []);

  const memoBody = React.useMemo(() => {
    return (
      <>
        <If condition={generating}>
          <FieldGroup marginBottom='10px'>
            <FieldLabel>Aguarde, gerando o relatório...</FieldLabel>
            <ProgressBar now={percent} label={`${percent}%`} variant='success' />
          </FieldGroup>
          <ReportAlert />
        </If>
        <If condition={!generating}>
          <Field
            type={FieldType.Select}
            label={'Pedidos'}
            selectProps={{
              options: [
                { id: MainFilterOptions.OnlyOpenOrders, name: 'Somente em aberto com pagto no período' },
                { id: MainFilterOptions.OnlyCancelledOrders, name: 'Somente cancelados com criação no período' },
                { id: MainFilterOptions.OpenOrClosedOrders, name: 'Abertos/fechados não cancelados com pagto no período' },
              ],
              displayField: 'name',
              keyField: 'id'
            }}
            value={mainFilterId}
            change={(value) => setMainFilterId(value)}
            marginTop={'0px'}
            marginRight='5px'
          />
          <FieldGroup>
            <FieldLabel>Período de abrangência do relatório</FieldLabel>
            <Row>
              <Field
                type={FieldType.DateTimePicker}
                value={initialDate}
                change={(value) => setInitialDate(value)}
                marginTop='0px'
                marginRight='5px'
              />
              <Field
                type={FieldType.DateTimePicker}
                value={finalDate}
                change={(value) => setFinalDate(value)}
                marginTop='0px'
                marginLeft='5px'
              />
            </Row>
          </FieldGroup>
          <Row>
            {loadingLocations ? null : (
            <Field
              type={FieldType.Select}
              label={strings.Location}
              selectProps={{
                options: [...locations.filter(l => userLocations.some(ulid => ulid === l.id)), { id:0, name:'Todas' }],
                displayField: 'name',
                keyField: 'id'
              }}
              value={locationId}
              change={(value) => setLocationId(value)}
              marginTop={'0px'}
              marginRight='5px'
              disabled={userLocations.length <= 1}
            />
            )}
            <Field
              type={FieldType.Select}
              label={strings.SeparationType}
              selectProps={{
                options: [
                  {id: 0, name: 'Todos'},
                  {id: SeparationType.Prescription, name: 'Com Grau'},
                  {id: SeparationType.NonPrescription, name: 'Sem Grau'},
                  {id: SeparationType.HomeTryOn, name: 'Zerezes em Casa'},
                ],
                displayField: 'name',
                keyField: 'id'
              }}
              value={separationType}
              change={(value) => setSeparationType(value)}
              marginTop={'0px'}
              marginLeft='5px'
            />
          </Row>
          <ReportAlert />
        </If>
      </>
    )
  }, [generating, percent, initialDate, finalDate, locationId, canceling, separationType, mainFilterId, loadingLocations, locations]);

  const memoFooter = React.useMemo(() => {
    return (
      <>
        <If condition={generating}>
          <Button text={strings.Cancel} disabled={!generating || canceling} click={() => cancelReport()} />
        </If>
        <If condition={!generating}>
          <Button text={strings.Confirm} click={() => confirmReport()} />
        </If>
      </>
    )
  }, [generating, percent, initialDate, finalDate, locationId, canceling, separationType, mainFilterId, loadingLocations, locations]);

  return (
    <Modal
      show={true}
      onHide={() => {
        if (generating || canceling) return;
        closeModal();
      }}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {strings.GroupedGeneralReport}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {memoBody}
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: 'center' }}>
        {memoFooter}
      </Modal.Footer>
    </Modal>
  );

  function closeModal() {
    mainActions.setNotWait(false);
    close();
  }

  function confirmReport() {
    setGenerating(true);
    setPercent(0);
    setCanceling(false);

    mainActions.setNotWait(true);

    stringDataList.current = [];
    cancel.current = false;

    const input = new GenerateGroupedGeneralOrdersReportInput();
    input.initialDate = moment(initialDate).format('YYYY-MM-DDTHH:mm:ss');
    input.finalDate = moment(finalDate).format('YYYY-MM-DDTHH:mm:ss');
    input.onlyOpenOrders = !mainFilterId || mainFilterId == MainFilterOptions.OnlyOpenOrders;
    input.onlyCancelledOrders = mainFilterId == MainFilterOptions.OnlyCancelledOrders;
    input.userId = session?.user?.id;
    if (locationId && locationId > 0) {
      input.locationId = locationId;
    } else if (locationId == 0) {
      input.locationIds = [ ...userLocations ];
    }
    input.separationType = separationType && +separationType > 0 ? +separationType : null;

    getOrderIdsForGroupedGeneralReportService.handle(input)
      .then(result => {
        orderIdList.current = result?.value?.orderIds || [];
        if (orderIdList.current.length === 0) {
          toastService.info('Relatório vazio!');
          setGenerating(false);
          return;
        }
        generateProgressiveGroupedGeneralOrderReport(0, 0);
      })
      .catch(error => {
        console.log('Error in confirmReport:', error);
        setGenerating(false);
        notifyProblemWhenGenerateReport();
      });
  }

  function generateProgressiveGroupedGeneralOrderReport(index: number, errors: number) {
    if (cancel.current || !orderIdList.current) {
      setGenerating(false);
      return;
    }

    if (index > orderIdList.current.length) {
      try {
        setPercent(100);
        setTimeout(() => downloadReport(), 1000);
      } catch (error) {
        console.log('Error in generateProgressiveGroupedGeneralOrderReport:', error);
        notifyProblemWhenGenerateReport();
      }
      return;
    }

    if (errors > 3) {
      setGenerating(false);
      notifyProblemWhenGenerateReport();
      return;
    }

    const ids = orderIdList.current.slice(index, step + index);

    if (ids.length === 0) {
      generateProgressiveGroupedGeneralOrderReport(step + index, 0);
      return;
    }

    let newPercent = (index / orderIdList.current.length) * 100;
    newPercent = +newPercent.toFixed(0);
    setPercent(newPercent);

    const input = new GenerateGroupedGeneralOrdersReportInput();
    input.orderIds = ids;
    input.initialDate = moment(initialDate).format('YYYY-MM-DDTHH:mm:ss');
    input.finalDate = moment(finalDate).format('YYYY-MM-DDTHH:mm:ss');
    input.onlyOpenOrders = !mainFilterId || mainFilterId == MainFilterOptions.OnlyOpenOrders;
    input.onlyCancelledOrders = mainFilterId == MainFilterOptions.OnlyCancelledOrders;
    input.userId = session?.user?.id;
    if (locationId && locationId > 0) {
      input.locationId = locationId;
    } else if (locationId == 0) {
      input.locationIds = [ ...userLocations ];
    }
    input.separationType = separationType && +separationType > 0 ? +separationType : null;
    input.includeHeaders = index === 0;
    input.returnStringData = true;
    generateGroupedGeneralOrdersReportService.handleCsvOrders(input)
      .then(result => {
        stringDataList.current.push(result?.value?.stringData || '');
        setTimeout(() => generateProgressiveGroupedGeneralOrderReport(step + index, 0), 500);
      })
      .catch(error => {
        console.log('Error in generateProgressiveGroupedGeneralOrderReport:', error);
        setTimeout(() => generateProgressiveGroupedGeneralOrderReport(index, errors + 1), 1000);
      });
  }

  function downloadReport() {
    const csvFile = stringDataList.current.map(value => Buffer.from(value, 'base64').toString()).join('');

    const separationTypeValue = separationType ? +separationType : null;
    let separationTypeDescription = '';
    switch (separationTypeValue) {
      case SeparationType.Prescription:
        separationTypeDescription = 'com grau';
        break;
      case SeparationType.NonPrescription:
        separationTypeDescription = 'não grau';
        break;
      case SeparationType.HomeTryOn:
        separationTypeDescription = 'zerezes em casa';
        break;
      default:
        separationTypeDescription = '';
    }

    const onlyOpenOrders = !mainFilterId || mainFilterId == MainFilterOptions.OnlyOpenOrders;
    const onlyCancelledOrders = mainFilterId == MainFilterOptions.OnlyCancelledOrders;

    const reportName = 'Relatório Geral Agrupado' +
      (locationId && locationId > 0 ? ' da loja ' + locations.find(l => +l.id === +locationId)?.name : '') +   
      (onlyOpenOrders ? ' com apenas pedidos em aberto e pagamento no período informado' : '') +
      (onlyCancelledOrders ? ' com apenas pedidos cancelados e criação no período informado' : '')+
      (separationTypeDescription ? ` do tipo ${separationTypeDescription}` : '')+
      (initialDate ? ' a partir de ' + moment(initialDate).format('DD-MM-YYYY-HH-mm') : '') +
      (finalDate ? ' até ' + moment(finalDate).format('DD-MM-YYYY-HH-mm') : '');

    reportService.downloadCsvFromString(csvFile, `${reportName}.csv`);

    setGenerating(false);
    closeModal();
  }

  function notifyProblemWhenGenerateReport() {
    mainActions.setNotWait(false);
    toastService.warning('Não consegui gerar o relatório. Tente novamente mais tarde e se o problema persistir avise o time Tech.');
  }

  function cancelReport() {
    setCanceling(true);
    cancel.current = true;
    mainActions.setNotWait(false);
  }

  function getLocations() {
    setLoadingLocations(true);
    mainActions.setNotWait(true);
    getLocationListService.handle()
      .then(result => setLocations(result?.value?.locations || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingLocations(false);
      });
  }

}

export { ModalGroupedGeneralOrderReport };